import { styled } from '@mui/material/styles';
import { PlanSummaryProgressBarTarget } from './PlanSummaryProgressBarTarget';
import { PlanSummaryProgressBarLegend } from './PlanSummaryProgressBarLegend';
import _map from 'lodash/map';
import _sum from 'lodash/sum';
import { InfoTooltip } from '../InfoTooltip';
import { formatCurrency } from '../../../utils';
import { useTranslation } from 'react-i18next';

const Container = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column'
}));

const Header = styled('header')<{ hasOriginalAmount: boolean }>(({ hasOriginalAmount = false }) => ({
    display: 'flex',
    gap: '41px',
    marginBottom: hasOriginalAmount ? '12px' : '25px'
}));

const TotalItem = styled('div')(({ theme }: any) => ({
    display: 'flex',
    flexDirection: 'column',
    '> div': {
        marginBottom: '2px',
        display: 'flex',
        alignItems: 'center'
    },
    p: {
        ...theme.typography.body.large
    },
    strong: {
        ...theme.typography.numbers.largeBold,
        marginBottom: '1px'
    },
    span: {
        ...theme.typography.numbers.small,
        color: '#595959'
    }
}));

const EditLink = styled('button')(({ theme }: any) => ({
    ...theme.typography.body.link.small,
    color: '#416AF4',
    marginBottom: 'auto',
    cursor: 'pointer'
}));

const BarContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    marginBottom: '18px',
    marginTop: '10px'
}));

const Bars = styled('div')(() => ({
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: '100%'
}));
const StyledEdits = styled('span')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
}));

const Bar = styled('div')<{ color: string; percentage: number }>(({ color, percentage }) => ({
    display: 'flex',
    backgroundColor: color,
    width: `${percentage}%`,
    height: '12px',
    borderRadius: '0',
    '&:last-child': {
        borderRadius: '0 100px 100px 0'
    },
    '&:first-child': {
        borderRadius: '100px 0px 0px 100px'
    }
}));

const LegendsContainer = styled('div')(({}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '33px'
}));

const StyledPipeSymbol = styled('span')(({}) => ({
    color: '#BFBFBF',
    lineHeight: 1,
    marginBottom: 'auto'
}));

export type PlanSummaryProgressBarValue = {
    label?: string;
    descText?: string;
    color: string;
    percentage: number;
};

type Props = {
    targetProgress: number;
    values: PlanSummaryProgressBarValue[];
    highlightedValues: {
        label: string;
        amount: number;
        originalAmount?: number;
        helperText?: string;
        helperTextProps?: {};
    }[];
    onClickEditGoals(): void;
    onClickReset(): void;
    isPDF?: boolean;
    showReset: boolean;
    isGoalRealistic?: boolean;
};

export const PlanSummaryProgressBar = ({
    targetProgress,
    values,
    highlightedValues,
    onClickEditGoals,
    onClickReset,
    showReset,
    isPDF,
    isGoalRealistic = true
}: Props) => {
    const missingProgress = 100 - _sum(_map(values, 'percentage'));
    const { t } = useTranslation();

    const hasOriginalAmount = !!highlightedValues?.some(({ originalAmount }) => !!originalAmount);
    return (
        <Container>
            <Header hasOriginalAmount={hasOriginalAmount}>
                {highlightedValues?.map(
                    ({ label, amount, originalAmount = 0, helperText = '', helperTextProps = {} }, index) => (
                        <TotalItem key={`highlightedValue_${index}`}>
                            <div>
                                <p>{label}</p>
                                {!isPDF && !!helperText && (
                                    <InfoTooltip
                                        width={312}
                                        placement="right-start"
                                        t={helperText}
                                        tProps={helperTextProps ?? {}}
                                    />
                                )}
                            </div>

                            <strong>{formatCurrency(amount)}</strong>
                            {!!originalAmount && <span>Original: {formatCurrency(originalAmount)}</span>}
                        </TotalItem>
                    )
                )}
                <StyledEdits>
                    {!isPDF && showReset ? (
                        <>
                            <EditLink onClick={onClickReset}>{t('RESET')}</EditLink>
                            <StyledPipeSymbol>|</StyledPipeSymbol>
                        </>
                    ) : null}
                    {!isPDF && <EditLink onClick={onClickEditGoals}>{t('EDIT_GOAL_DETAILS')}</EditLink>}
                </StyledEdits>
            </Header>
            {isGoalRealistic && (
                <>
                    <BarContainer>
                        <Bars>
                            {values.map(({ percentage, color }, index) => (
                                <Bar key={`bar_${index}`} color={color} percentage={percentage} />
                            ))}

                            {missingProgress > 0 && <Bar color="#E4E5E8" percentage={missingProgress} />}
                        </Bars>

                        <PlanSummaryProgressBarTarget progress={targetProgress} isPDF={isPDF} />
                    </BarContainer>

                    <LegendsContainer>
                        {values
                            .filter(({ label }) => !!label)
                            .map(({ label, color, descText }, index) => (
                                <PlanSummaryProgressBarLegend
                                    key={`legend_${index}`}
                                    label={label!}
                                    color={color}
                                    descText={descText}
                                />
                            ))}
                    </LegendsContainer>
                </>
            )}
        </Container>
    );
};
